// src/client/screens/borrower/RepayScreen.tsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import { useActiveLoan } from '@client/hooks/useLoan';
import { useInitiateRepayment } from '@client/hooks/useRepayment';
import { subscribeToPaymentStatus } from '@client/services/paymentService';
import type { MobileMoneyProvider } from '@/constants/config';
import type { PaymentFixture } from '@client/lib/fixtures';
import ScreenShell from '@client/components/ScreenShell';
import Card from '@client/components/Card';
import Money from '@client/components/Money';
import Button from '@client/components/Button';
import PaymentMethodPicker from '@client/components/PaymentMethodPicker';
import { useToast } from '@client/components/ToastProvider';

const STATUS_LABEL: Record<PaymentFixture['status'], string> = {
  initiated: 'Initiated…',
  pending:   'Awaiting your USSD confirmation…',
  completed: 'Payment confirmed ✓',
  failed:    'Payment failed',
  timeout:   'Timed out — please try again',
  reconciled: 'Reconciled ✓',
};

export default function RepayScreen() {
  const { data: loan } = useActiveLoan();
  const repay = useInitiateRepayment();
  const [provider, setProvider] = useState<MobileMoneyProvider>('mtn_momo');
  const [pendingPayment, setPendingPayment] = useState<{ id: string; status: PaymentFixture['status'] } | null>(null);
  const navigate = useNavigate();
  const toast = useToast();

  // Subscribe to real-time updates once a payment is initiated
  useEffect(() => {
    if (!pendingPayment) return;
    const unsub = subscribeToPaymentStatus(pendingPayment.id, (status) => {
      setPendingPayment((prev) => prev ? { ...prev, status } : null);
    });
    return unsub;
  }, [pendingPayment?.id]);

  // Auto-navigate home 1.5s after completion
  useEffect(() => {
    if (pendingPayment?.status !== 'completed') return;
    const t = setTimeout(() => navigate(ROUTES.home, { replace: true }), 1500);
    return () => clearTimeout(t);
  }, [pendingPayment?.status, navigate]);

  if (!loan) {
    return (
      <ScreenShell title="Repay">
        <Card>
          <p className="small">Nothing to repay right now.</p>
        </Card>
      </ScreenShell>
    );
  }

  const submit = () => {
    repay.mutate(
      { loanId: loan.id, amount: loan.outstanding_ngwee, provider },
      {
        onSuccess: (payment) => {
          setPendingPayment({ id: payment.id, status: payment.status });
          toast.show('Check your phone for the USSD prompt', 'success');
        },
        onError: () => toast.show('Could not initiate payment', 'error'),
      },
    );
  };

  return (
    <ScreenShell title="Repay">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Card title={loan.loan_number}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span className="eyebrow">Outstanding</span>
            <strong style={{ fontSize: 22 }}>
              <Money amount={loan.outstanding_ngwee} />
            </strong>
          </div>
        </Card>

        {pendingPayment ? (
          <Card title="Payment status">
            <p style={{ textAlign: 'center', padding: '12px 0' }}>
              {STATUS_LABEL[pendingPayment.status]}
            </p>
            {(pendingPayment.status === 'failed' || pendingPayment.status === 'timeout') && (
              <Button block onClick={() => setPendingPayment(null)}>Try again</Button>
            )}
          </Card>
        ) : (
          <>
            <Card title="Pay with">
              <PaymentMethodPicker value={provider} onChange={setProvider} />
            </Card>
            <Button block size="lg" loading={repay.isPending} onClick={submit}>
              Pay now
            </Button>
          </>
        )}
      </div>
    </ScreenShell>
  );
}
